import { useState, useEffect } from 'react';

/**
 * Hook for PWA install functionality
 * Detects if app is installable and provides install prompt
 */
export const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();

      // Save the event for triggering later
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  /**
   * Trigger the install prompt
   * @returns {Promise<boolean>} - True if user accepted, false if dismissed
   */
  const promptInstall = async () => {
    if (!deferredPrompt) {
      console.warn('No install prompt available');
      return false;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user's response
    const { outcome } = await deferredPrompt.userChoice;

    // Clear the deferred prompt
    setDeferredPrompt(null);
    setIsInstallable(false);

    return outcome === 'accepted';
  };

  /**
   * Dismiss the install prompt (don't show again for this session)
   */
  const dismissPrompt = () => {
    setIsInstallable(false);
    // Store in sessionStorage to not show again this session
    sessionStorage.setItem('pwa-install-dismissed', 'true');
  };

  /**
   * Check if prompt was dismissed this session
   */
  const wasDismissed = () => {
    return sessionStorage.getItem('pwa-install-dismissed') === 'true';
  };

  return {
    isInstallable: isInstallable && !wasDismissed(),
    isInstalled,
    promptInstall,
    dismissPrompt,
  };
};

export default usePWAInstall;
